import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { ANALYSIS_TIMEOUT_MS } from '../lib/imagePrep';
import { confirmAnalysisCharge } from '../lib/analysisCharge';
import { useAuth } from './AuthContext';
import { captureInference, reportIfSlow, captureAnalysisRun } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnalysisContext = createContext();

export const useAnalysis = () => {
  const context = useContext(AnalysisContext);
  if (!context) {
    throw new Error('useAnalysis must be used within an AnalysisProvider');
  }
  return context;
};

export const AnalysisProvider = ({ children }) => {
  const { token, refreshUsage, usage } = useAuth();
  
  // Pending items ready for review (analysis complete, not yet saved)
  const [pendingItems, setPendingItems] = useState([]);
  
  // Items currently being analyzed (in queue)
  const [analysisQueue, setAnalysisQueue] = useState([]);
  
  // Draft items saved to DB but not verified
  const [draftCount, setDraftCount] = useState(0);
  
  // Analysis in progress flag
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Recently finished photos, kept so progress stays on screen for a moment
  const [completedPhotos, setCompletedPhotos] = useState([]);
  const clearCompletedPhotos = useCallback(() => setCompletedPhotos([]), []);
  
  // Jobs run in parallel, so count them rather than guessing from the queue length
  const inFlight = useRef(0);
  const seq = useRef(0);

  // Refresh draft count from server
  const refreshDraftCount = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/inventory/drafts/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDraftCount(response.data.count || 0);
    } catch (err) {
      console.error('Failed to refresh draft count:', err);
    }
  }, [token]);
  
  // Fetch draft count on mount and when token changes
  useEffect(() => {
    refreshDraftCount();
  }, [refreshDraftCount]);

  // Add image to analysis queue and start background processing
  const queueForAnalysis = useCallback(async (imageData, room, metadata, analysisImage, label) => {
    const queueId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    seq.current += 1;
    const order = seq.current;
    inFlight.current += 1;
    
    // Add to queue immediately
    setAnalysisQueue(prev => [...prev, {
      id: queueId,
      status: 'analyzing',
      room,
      label: label || 'Photo',
      order,
      timestamp: new Date().toISOString()
    }]);
    
    setIsAnalyzing(true);

    try {
      // Call API in background
      const startedAt = performance.now();
      const response = await axios.post(
        `${API_URL}/api/analyze/single`,
        { image_base64: analysisImage || imageData, room: room, capture_metadata: metadata || null },
        { headers: { Authorization: `Bearer ${token}` }, timeout: ANALYSIS_TIMEOUT_MS }
      );
      const totalMs = Math.round(performance.now() - startedAt);
      await confirmAnalysisCharge(API_URL, token, response.data.charge_id);

      const items = (response.data.items || []).map(item => ({
        ...item,
        image_base64: imageData,
        photo_metadata: { ...(metadata || {}), ...(response.data.photo_metadata || {}) },
        room: room,
        _queueId: queueId,
        _isEdited: false,
        _status: 'pending_review',
        _addedAt: new Date().toISOString()
      }));

      const ai = response.data.ai || {};
      items.forEach(item => captureInference({
        predicted_outcome: item.category,
        confidence_score: item.confidence_score,
        model_latency_ms: ai.latency_ms,
        round_trip_latency_ms: totalMs,
        model: ai.model,
        surface: 'background_photo',
        context: 'pre_loss',
        room,
        item_name: item.name,
        items_found: items.length
      }));
      reportIfSlow({
        model_latency_ms: ai.latency_ms,
        round_trip_latency_ms: totalMs,
        model: ai.model,
        surface: 'background_photo',
        context: 'pre_loss',
        predicted_outcome: items[0]?.category,
        apiUrl: API_URL,
        token
      });

      const freshUsage = await refreshUsage();
      captureAnalysisRun({ usage: freshUsage || usage, surface: 'background_photo', photos: 1 });

      // Move from "in progress" to "finished" so the user can see what each photo produced
      setAnalysisQueue(prev => prev.filter(q => q.id !== queueId));
      setCompletedPhotos(prev => [
        ...prev.slice(-9),
        { key: queueId, label: label || 'Photo', order, status: items.length ? 'done' : 'unclear', itemCount: items.length }
      ]);

      // Auto-save as drafts to database
      let savedCount = 0;
      for (const item of items) {
        try {
          await axios.post(
            `${API_URL}/api/inventory/draft`,
            {
              name: item.name,
              category: item.category,
              make: item.make,
              model: item.model,
              specifications: item.specifications,
              suggested_retailer: item.suggested_retailer,
              replacement_recommendation: item.replacement_recommendation,
              estimated_cost: parseFloat(item.estimated_cost) || 0,
              condition: item.condition,
              room: item.room || room,
              notes: '',
              image_base64: item.image_base64 || null,
              photo_metadata: item.photo_metadata || null,
              confidence_score: item.confidence_score ?? null,
              ai_model: ai.model || null,
              status: 'draft'
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          savedCount++;
        } catch (err) {
          console.error('Failed to auto-save draft:', err);
        }
      }

      // Update draft count
      setDraftCount(prev => prev + savedCount);

      return { success: true, itemCount: items.length };
    } catch (err) {
      console.error('Analysis failed:', err);
      // Mark as failed in queue
      setAnalysisQueue(prev => prev.filter(q => q.id !== queueId));
      setCompletedPhotos(prev => [
        ...prev.slice(-9),
        { key: queueId, label: label || 'Photo', order, status: 'failed', itemCount: 0 }
      ]);
      return { success: false, error: err.message };
    } finally {
      inFlight.current = Math.max(0, inFlight.current - 1);
      if (inFlight.current === 0) {
        setIsAnalyzing(false);
      }
    }
  }, [token, refreshUsage, usage]);

  // Add items directly to pending (from manual analysis)
  const addToPending = useCallback((items) => {
    const itemsWithMeta = items.map(item => ({
      ...item,
      _status: 'pending_review',
      _addedAt: new Date().toISOString()
    }));
    setPendingItems(prev => [...prev, ...itemsWithMeta]);
  }, []);

  // Remove item from pending
  const removeFromPending = useCallback((index) => {
    setPendingItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Update pending item
  const updatePendingItem = useCallback((index, updates) => {
    setPendingItems(prev => prev.map((item, i) => 
      i === index ? { ...item, ...updates, _isEdited: true } : item
    ));
  }, []);

  // Clear all pending items (after save)
  const clearPending = useCallback(() => {
    setPendingItems([]);
  }, []);

  // Clear specific items by indices
  const clearPendingByIndices = useCallback((indices) => {
    setPendingItems(prev => prev.filter((_, i) => !indices.includes(i)));
  }, []);

  // Clear failed items from queue
  const clearFailedFromQueue = useCallback(() => {
    setAnalysisQueue(prev => prev.filter(q => q.status !== 'failed'));
  }, []);

  // Get counts
  const pendingCount = pendingItems.length;
  const queueCount = analysisQueue.length;
  const totalPendingCount = pendingCount + queueCount + draftCount;

  const value = useMemo(() => ({
    // State
    pendingItems,
    analysisQueue,
    completedPhotos,
    draftCount,
    isAnalyzing,
    pendingCount,
    queueCount,
    totalPendingCount,
    
    // Actions
    queueForAnalysis,
    clearCompletedPhotos,
    addToPending,
    removeFromPending,
    updatePendingItem,
    clearPending,
    clearPendingByIndices,
    clearFailedFromQueue,
    refreshDraftCount,
    setPendingItems,
    setDraftCount
  }), [
    pendingItems,
    analysisQueue,
    draftCount,
    isAnalyzing,
    pendingCount,
    queueCount,
    totalPendingCount,
    queueForAnalysis,
    addToPending,
    removeFromPending,
    updatePendingItem,
    clearPending,
    clearPendingByIndices,
    clearFailedFromQueue,
    refreshDraftCount, completedPhotos, clearCompletedPhotos]);

  return (
    <AnalysisContext.Provider value={value}>
      {children}
    </AnalysisContext.Provider>
  );
};

export default AnalysisContext;
